import { energySystemCollection } from '@/reducers/energySystems'
import { EnergySystem, EnergySystemStatus } from '@/types/energySystem'
import { Card, Icon, Text } from '@/components'
import { BatteryProgressBar } from './BatteryProgressBar'
import { match } from 'ts-pattern'

type Props = {
  energySystemId: string
}

function useViewModel(viewModelProps: { energySystem: EnergySystem }) {
  const batteryPercentage =
    viewModelProps.energySystem.state_of_charge_percentage
  const status = viewModelProps.energySystem.status

  return {
    batteryPercentage,
    status,
  }
}

function BatteryStatusComponent(props: Parameters<typeof useViewModel>[0]) {
  const { batteryPercentage, status } = useViewModel(props)

  if (batteryPercentage === null || status === null) {
    return null
  }

  const { icon, color, statusText } = match(status)
    .with(EnergySystemStatus.Charging, () => ({
      icon: 'Plug' as const,
      color: 'bg-themed-green-500',
      statusText: 'Charging',
    }))
    .with(EnergySystemStatus.Discharging, () => ({
      icon: 'Plug' as const,
      color: 'bg-themed-blue-500',
      statusText: 'Discharging',
    }))
    .with(EnergySystemStatus.Idle, () => ({
      icon: 'Plug' as const,
      color: 'bg-themed-grey-400',
      statusText: 'Standby',
    }))
    .exhaustive()

  return (
    <Card className="p-6">
      <div className="flex flex-col gap-6">
        <div className="flex gap-3">
          <Icon name={icon} size={20} color="rgb(55, 65, 81)" />
          <Text variant="h3">{statusText}</Text>
        </div>

        <BatteryProgressBar percentage={batteryPercentage} color={color} />
      </div>
    </Card>
  )
}

function BatteryStatusLoading() {
  return (
    <Card className="p-6">
      <div className="flex flex-col gap-6">
        <div className="flex gap-3">
          <div className="w-5 h-5 bg-gray-300 rounded animate-pulse" />
          <div className="w-20 h-6 bg-gray-300 rounded animate-pulse" />
        </div>

        <BatteryProgressBar isLoading={true} />
      </div>
    </Card>
  )
}

export function BatteryStatus(props: Props) {
  const { data: energySystems, isLoadingOrIdle } =
    energySystemCollection.useFetch('energy-systems')
  const energySystem = energySystems?.find(
    (energySystem) => energySystem.id === props.energySystemId,
  )

  if (isLoadingOrIdle) {
    return <BatteryStatusLoading />
  }

  if (!energySystem) {
    return null
  }

  return <BatteryStatusComponent energySystem={energySystem} />
}
